import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Info, CheckCircle, AlertTriangle, AlertOctagon, ChevronLeft, ChevronRight, X
} from 'lucide-react';
import noticeData from '../data/notice.json';

// --- Styled Components ---

const BannerContainer = styled(motion.div)`
    position: fixed;
    top: 80px; /* Below Header */
    left: 0;
    right: 0;
    background: #0f172a; /* Slate-900 */
    color: white;
    z-index: 900;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    height: 60px; /* Fixed height for consistency */
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ContentWrapper = styled.div`
    max-width: 1280px;
    width: 100%;
    margin: 0 auto;
    padding: 0 56px; /* Space for arrows */
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SlideContainer = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;

const NoticeItem = styled(motion.div)`
    position: absolute;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    font-size: 0.9rem;
    font-weight: 500;
    white-space: nowrap;
    
    @media (max-width: 768px) {
        white-space: normal;
        text-align: center;
        font-size: 0.8rem;
        line-height: 1.3;
        padding: 0 10px;
    }
`;

const Badge = styled.span`
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &.info { background: rgba(59, 130, 246, 0.2); color: #60a5fa; }
    &.success { background: rgba(16, 185, 129, 0.2); color: #34d399; }
    &.warning { background: rgba(245, 158, 11, 0.2); color: #fbbf24; }
    &.error { background: rgba(239, 68, 68, 0.2); color: #f87171; }
`;

const NavButton = styled.button`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    z-index: 10;

    &:hover {
        background: rgba(255,255,255,0.1);
        color: white;
    }

    &.left { left: 12px; }
    &.right { right: 12px; }

    &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

// --- Component ---

const NoticeBanner = ({ onNoticesChange }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [notices, setNotices] = useState([]);

    // Load and filter notices
    useEffect(() => {
        const activeNotices = noticeData.notices
            .filter(n => n.isActive)
            .filter(n => {
                const now = new Date();
                return now >= new Date(n.startDate) && now <= new Date(n.endDate);
            })
            .sort((a, b) => a.priority - b.priority)
            .slice(0, noticeData.settings.maxNotices);

        setNotices(activeNotices);
    }, []);

    // Notify parent about visibility
    useEffect(() => {
        if (onNoticesChange) {
            onNoticesChange(notices.length > 0);
        }
    }, [notices.length, onNoticesChange]);

    // Auto-rotate
    useEffect(() => {
        if (!noticeData.settings.autoRotate || notices.length <= 1) return;
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % notices.length);
        }, noticeData.settings.rotationInterval);
        return () => clearInterval(interval);
    }, [notices.length]);

    const getIcon = (type) => {
        switch (type) {
            case 'success': return <CheckCircle size={14} />;
            case 'warning': return <AlertTriangle size={14} />;
            case 'error': return <AlertOctagon size={14} />;
            default: return <Info size={14} />;
        }
    };

    const nextNotice = () => setCurrentIndex((prev) => (prev + 1) % notices.length);
    const prevNotice = () => setCurrentIndex((prev) => (prev === 0 ? notices.length - 1 : prev - 1));

    if (notices.length === 0) return null;

    const currentNotice = notices[currentIndex];

    return (
        <BannerContainer
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 60, opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
            <ContentWrapper>
                {notices.length > 1 && (
                    <NavButton className="left" onClick={prevNotice}>
                        <ChevronLeft size={18} />
                    </NavButton>
                )}

                <SlideContainer>
                    <AnimatePresence mode='wait'>
                        <NoticeItem
                            key={currentNotice.id}
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -20, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Badge className={currentNotice.type}>
                                {getIcon(currentNotice.type)}
                                {currentNotice.title}
                            </Badge>
                            <span>{currentNotice.message}</span>
                        </NoticeItem>
                    </AnimatePresence>
                </SlideContainer>

                {notices.length > 1 && (
                    <NavButton className="right" onClick={nextNotice}>
                        <ChevronRight size={18} />
                    </NavButton>
                )}
            </ContentWrapper>
        </BannerContainer>
    );
};

export default NoticeBanner;